import React, { memo } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  ListingViewsCount,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const DEFAULT_LISTING_CARD_SIZES = [
  '(max-width: 600px) calc(100vw - 48px)',
  '(max-width: 992px) calc(50vw - 40px)',
  '160px',
].join(', ');

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 100 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const biddingActive = publicData?.biddingEnabled && publicData?.listingOpenStatus !== 'pending';
  const hasGuidePrice = price && price.amount > 0;

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  if (biddingActive && !hasGuidePrice) {
    return (
      <div className={css.priceContainer}>
        <div className={css.price}>
          <span className={css.priceValue}>
            <FormattedMessage id="listingCard.makeOffer" />
          </span>
        </div>
        <div className={css.biddingAvailableSecondary}></div>
      </div>
    );
  }

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.priceContainer} title={priceTitle}>
      <div className={css.price}>
        {biddingActive ? (
          // For bidding listings, price is a guide price (not "per unit" and not "from").
          priceValue
        ) : hasMultiplePriceVariants ? (
          <FormattedMessage
            id="ListingCard.priceStartingFrom"
            values={{ priceValue, pricePerUnit }}
          />
        ) : (
          <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
        )}
      </div>
      {biddingActive && hasGuidePrice ? (
        <div className={css.biddingAvailableSecondary}>
          <FormattedMessage id="listingCard.biddingAvailableSecondary" />
        </div>
      ) : <div className={css.biddingAvailableSecondary}></div>}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const preferredSquareVariants = firstImage
    ? ['square-small', 'square-small2x'].filter(k => firstImage?.attributes?.variants?.[k])
    : [];

  // Fallback to other suitable variants if the preferred 4:3 variants are not available
  const fallbackVariants = firstImage && variants.length === 0
    ? Object.keys(firstImage?.attributes?.variants)
      .filter(k => k.includes('crop') || k.includes('scaled') || k.includes('landscape'))
      .slice(0, 2) // Use first 2 suitable variants
    : [];

  const finalVariants =
    variants.length > 0
      ? variants
      : preferredSquareVariants.length > 0
        ? preferredSquareVariants
        : fallbackVariants;

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={finalVariants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = memo(props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes = DEFAULT_LISTING_CARD_SIZES,
    setActiveListing,
    showAuthorInfo = true,
    showBiddingBadge = true,
    renderListingDetails,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;
  return (
    <div id={slug + '-' + id}>
      <NamedLink
        className={classes}
        name="ListingPage"
        params={{ id, slug }}
        to={{ state: { forceScrollTop: true } }}
      >
        {publicData?.featured && (
          <div className={css.featureTag}>
            <FormattedMessage id="listingCard.featureListingTag" />
          </div>
        )}
        {/* {showBiddingBadge && publicData?.biddingEnabled && publicData?.listingOpenStatus !== 'pending' && (
          <div className={css.biddingTag}>
            <FormattedMessage id="listingCard.biddingTag" />
          </div>
        )} */}
        <ListingCardImage
          renderSizes={renderSizes}
          title={title}
          currentListing={currentListing}
          config={config}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={'square-small'}
          style={cardStyle}
          showListingImage={showListingImage}
        />

        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.titleNFav}>
              {showListingImage && (
                <div className={css.title}>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </div>
              )}
              <ListingViewsCount publicData={publicData} />

              {/* <div className={classNames(css.favHeart, false && css.favHeatSelected)}>
              <IconCard brand="favHeart" />
            </div> */}
            </div>
          </div>
          <div className={css.authorNprice}>
            {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null}
            <PriceMaybe
              price={price}
              publicData={publicData}
              config={config}
              intl={intl}
              listingTypeConfig={foundListingTypeConfig}
            />
          </div>

          {renderListingDetails ? renderListingDetails(currentListing) : null}

          {(() => {
            const isBootleg = publicData?.categoryLevel2 && publicData.categoryLevel2.toLowerCase().includes('bootleg');
            if (!isBootleg) return null;
            return (
              <div className={css.bootlegTag}>
                Bootleg
              </div>
            );
          })()}
        </div>
      </NamedLink>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return (
    prevProps.listing?.id?.uuid === nextProps.listing?.id?.uuid &&
    prevProps.listing?.attributes?.title === nextProps.listing?.attributes?.title &&
    prevProps.listing?.attributes?.price?.amount === nextProps.listing?.attributes?.price?.amount &&
    prevProps.listing?.attributes?.publicData?.dimensions ===
      nextProps.listing?.attributes?.publicData?.dimensions &&
    prevProps.listing?.attributes?.publicData?.custom_dimensions ===
      nextProps.listing?.attributes?.publicData?.custom_dimensions &&
    prevProps.listing?.attributes?.publicData?.custom_dimensions_description ===
      nextProps.listing?.attributes?.publicData?.custom_dimensions_description &&
    prevProps.renderSizes === nextProps.renderSizes &&
    prevProps.showAuthorInfo === nextProps.showAuthorInfo &&
    prevProps.renderListingDetails === nextProps.renderListingDetails
  );
});

export default ListingCard;
